import { GuestDetails } from '@tv3/models/guest/guest-details'
import { BookingEngineCost } from '@tv3/models/inquiry/booking-engine-cost'
import {
  asUTCEpoch,
  epochToView,
  epochToViewUTC,
  Expose,
  getNightsBetweenArrivalDepart,
  isSomething,
  isUUIDLose,
  plainToClass,
  Rental,
  Serializable,
  Transform,
  Type,
} from '@tokeet-frontend/tv3-platform'
import { Guest } from '@tv3/store/guest/guest.model'
import { InquiryBilling } from '@tv3/models/inquiry/inquiry-billing'
import { NoteResponse } from '@tv3/store/note/note.model'
import { Message } from '@tv3/store/message/message.model'
import * as moment from 'moment'
import * as R from 'ramda'
import { timeAgo } from '@tv3/pipes/utility/time-ago.pipe'
import { Invoice, upgradeInvoiceTax } from '@tokeet-frontend/invoices'
import { InquiryCost } from '@tokeet/cost-resolver'

export const DefaultInquiryColor = '#ddd'

import { v4 as uuid } from 'uuid'

import * as lodash from 'lodash'
import { InquirySourceType, InquirySourceView, parseInquirySource } from './inquiry-source-view'
import { ReviewCategoryRatingForAI } from '@tokeet-frontend/reviews'
import {
  ExpediaPrice,
  BDCPrice,
  MarriottPrice,
  VRBOPriceItem,
  TiketPrice,
  TripPrice,
  RentalsUnitedPrice,
  AgodaPrice,
  AirbnbPrice,
} from './channel_price.model'
import { AirbnbReservationAlterationItem, VRBOReservationAlterationItem } from '@tokeet-frontend/channels'

export enum CheckInStatus {
  CheckedIn = 'checked-in',
  CheckedOut = 'checked-out',
  Arriving = 'pending',
}

export interface EVisitorCheckinStayRequest {
  arrival: number
  departure: number
  arrivalType: string
  borderCrossing?: number
  passageDate?: number
}

export interface EVisitorCheckinHostRequest {
  paymentCategory: number
  facilityCode: number
  serviceType: string
  accommodationUnitType?: string
  residenceAddress?: string
}

export interface EVisitorCheckinGuestRequest {
  firstName: string
  middleName?: string
  lastName: string
  email?: string
  phone?: string
  gender: string
  citizenshipCountryCode: string
  dateOfBirth: number
  birthCountryCode: string
  cityOfBirth?: string
  residenceCountryCode: string
  cityOfResidence: string
  documentType: number | string
  documentId: string
}

export interface EVisitorCheckinRequest {
  id: string
  inquiry_pkey: string
  guest: EVisitorCheckinGuestRequest
  stay: EVisitorCheckinStayRequest
  host: EVisitorCheckinHostRequest
}

export interface EVisitorCheckinResponse {
  statusCode: number
  data?: any
  headers?: Record<string, string>
}

export interface EVisitorCheckinRequestResponse {
  guest_pkey?: string
  created?: number
  request?: EVisitorCheckinRequest
  response?: EVisitorCheckinResponse
}

export interface EVisitorRequestResponse {
  guest_pkey?: string
  created?: number
  request?: any
  response?: any
}

export interface InquiryEVisitor {
  checkins?: EVisitorCheckinRequestResponse[]
  checkouts?: EVisitorRequestResponse[]
  cancels?: EVisitorRequestResponse[]
}

export enum InquiryPaymentStatus {
  Unpaid = 0,
  PartialPaid = 1,
  Paid = 2,
}

export interface InquiryAutomationSettings {
  ignore_message?: 0 | 1
  ignore_review?: 0 | 1
}

export type InquiryAutomationPreference = keyof InquiryAutomationSettings

// TV4-6226: affiliate / referral id captured from the booking link (`ref` parameter) on direct bookings
export interface InquiryReferral {
  id: string
  // channel that captured the id, e.g. 'tokeetwebsite' or 'webready'
  source?: string
  // epoch timestamp when the id was captured
  captured?: number
}

export interface InquiryAttributes {
  readonly api: string
  readonly alteration_requested?: string
  tags?: string[]
  is_checked_in?: 0 | 1
  is_checked_out?: 0 | 1
  color?: string
  additionalGuests?: string[]
  payment_status?: InquiryPaymentStatus
  double_booking?: number
  double_booking_source?: string
  referral?: InquiryReferral
  bdc_rtb?: BdcRtbRequest
}

// TV4-6322: a Booking.com Request to Book request, stored by the backend sync on the request inquiry.
export type BdcRtbRequestStatus =
  | 'CREATED'
  | 'APPROVED'
  | 'REJECTED'
  | 'BOOKED'
  | 'EXPIRED_PENDING_APPROVAL'
  | 'EXPIRED_AFTER_APPROVAL'
  | 'FAILED_TO_PROCESS'
  | 'WITHDRAWN_PENDING_APPROVAL'

// Guest signals as Booking.com sends them; null or missing means unknown.
export interface BdcRtbGuestInfo {
  emailVerified?: 0 | 1 | null
  phoneVerified?: 0 | 1 | null
  domestic?: 0 | 1 | null
  hasNoShow?: 0 | 1 | null
  hasPastMisconduct?: 0 | 1 | null
  hasStayWithPartnerBefore?: 0 | 1 | null
  completedStays?: string | null
  registeredSince?: string | null
}

export interface BdcRtbRequest {
  request_id: string
  property_id: string
  room_id?: string
  request_type: 'RTB' | 'INQUIRY'
  request_status: BdcRtbRequestStatus
  expires_at?: number
  created_at?: number
  status_updated_at?: number
  guest_message?: string
  guest_info?: BdcRtbGuestInfo
  total_price?: { amount: number; currency: string }
  reservation_id?: string
  rejection_reason?: string
}

// TV4-5427: set by the backend when the payment gateway rejects the guest card on import
// (status 'payment.card.rejected'), cleared when a card attaches successfully.
export interface InquiryCardPaidStatus {
  status: string
  error?: string
  touch?: number
}

export class Inquiry extends Serializable<Inquiry> {
  @Expose({ name: 'pkey' })
  id: string

  @Expose({ name: 'guest_depart' })
  guestDepart: number

  @Expose({ name: 'ref_id' })
  refId: string

  @Expose({ name: 'inquiry_id' })
  inquiryId: string

  @Expose({ name: 'guest_details' })
  @Type(() => GuestDetails)
  guestDetails: GuestDetails

  @Expose({ name: 'booking_engine' })
  @Type(() => BookingEngineCost)
  bookingEngine: BookingEngineCost

  @Expose({ name: 'check_out' })
  checkOut: number

  @Expose({ name: 'rental_id' })
  rentalId: string

  @Type(() => Rental)
  rental: Rental

  @Expose({ name: 'booking_id' })
  bookingId: string

  @Expose({ name: 'num_child' })
  numChild: number

  @Expose({ name: 'num_adults' })
  numAdults: number

  num_pets?: number
  num_infants?: number

  @Expose({ name: 'inquiry_source' })
  inquirySource: string

  sourceType: InquirySourceType
  sourceView: InquirySourceView

  alteration_requested?: VRBOReservationAlterationItem | AirbnbReservationAlterationItem
  abb_price: AirbnbPrice
  agoda_price: AgodaPrice
  expedia_price: ExpediaPrice
  bdc_price: BDCPrice
  marriott_price: MarriottPrice
  vrbo_price: VRBOPriceItem[]
  tiket_price: TiketPrice
  price_details: TripPrice | VRBOPriceItem[]
  ctrip_price: TripPrice
  rentalsunited_price: RentalsUnitedPrice

  @Expose({ name: 'convo_id' })
  convoId: string

  @Expose({ name: 'received_on' })
  receivedOn: number

  @Expose({ name: 'guest_arrive' })
  guestArrive: number

  @Expose({ name: 'guest_id' })
  guestId: string

  @Expose({ name: 'check_in' })
  checkIn: number

  @Expose({ name: 'processed_date' })
  processedDate: number

  @Expose({ name: 'automata_logs' })
  automataLogs: object[]

  @Expose({ name: 'message_txt' })
  messageTxt: string

  @Expose({ name: 'message_id' })
  messageId: string

  @Expose({ name: 'property_id' })
  propertyId: number

  @Expose({ name: 'inquiry_logs' })
  inquiryLogs: object[]

  @Expose({ name: 'last_message_at' })
  lastMessageAt: number

  @Expose({ name: 'reference_id' })
  referenceId: number

  @Expose({ name: 'guest_name' })
  guestName: string

  @Expose({ name: 'disable_guest_portal' })
  disableGuestPortal: 0 | 1

  @Type(() => Guest)
  guest: Guest

  ratings?: ReviewCategoryRatingForAI[]

  replyto: string
  quotes: string
  logs: object[]
  read: number
  archived: number
  closed: number
  touch: number
  created: number
  canceled: number
  attributes: InquiryAttributes
  evisitor?: InquiryEVisitor
  evisitor_guest_ids?: string[]
  attachments?: string[]
  booked: number
  account: number
  cost: string

  formulaCost?: number

  skip: boolean //@todo - remove this

  notes: NoteResponse[]

  paid: number // mark as paid
  inhouse: number // mark as in-house
  payment_status?: InquiryPaymentStatus // payment status from invoices
  card_paid_status?: InquiryCardPaidStatus // last failed attempt to attach the guest card

  sentto: string
  followup: number

  @Type(() => Invoice)
  invoices: Invoice[]

  @Transform((messages) => messages?.map((m) => Message.deserialize(m)) || [])
  messages: Message[]

  @Type(() => InquiryBilling)
  billing: InquiryBilling

  status: InquiryStatus
  links: string[]
  payout: number | string

  discountFees: any

  automation?: InquiryAutomationSettings

  /* View Helpers */
  charges: InquiryCost
  rentalView: string
  touchView: string
  receivedOnView: string
  guestNameView: string
  arriveView: string
  departView: string
  nightsView: number
  guestsView: string
  bookedView: string
  createdView: string
  channelBookingIdView: string
  paymentStatusView: InquiryPaymentStatus

  /* Thread View Helpers */
  lastMessageAgo = ''
  checkInStatus: CheckInStatus = CheckInStatus.Arriving
  guestCount: number

  guestArriveTimeView?: string
  guestDepartTimeView?: string

  static deserialize(data: any): Inquiry {
    if (R.is(String, data.touch)) {
      data.touch = asUTCEpoch(data.touch)
    }
    const inquiry: Inquiry = plainToClass<Inquiry, Inquiry>(Inquiry, data)
    if (inquiry.bookingEngine) {
      inquiry.bookingEngine = BookingEngineCost.deserialize(data.booking_engine)
    }

    inquiry.checkIn = parseInt((<any>inquiry).checkIn, 10)
    inquiry.checkOut = parseInt((<any>inquiry).checkOut, 10)
    inquiry.numAdults = parseInt((<any>inquiry).numAdults, 10)
    inquiry.numChild = parseInt((<any>inquiry).numChild, 10)

    inquiry.guestArrive = parseInt((<any>inquiry).guestArrive, 10)
    inquiry.guestDepart = parseInt((<any>inquiry).guestDepart, 10)

    // TV4-5863: numeric status flags can arrive as JSON strings (the API's save()
    // diagnostics stringify the live document before some endpoints serialize it,
    // e.g. canceled:"0" from /inquiry/inhouse) — coerce so truthiness checks on
    // these fields don't light up the wrong status icons
    inquiry.booked = Number(inquiry.booked) || 0
    inquiry.canceled = Number(inquiry.canceled) || 0
    inquiry.paid = Number(inquiry.paid) || 0
    inquiry.inhouse = Number(inquiry.inhouse) || 0

    if (isSomething(inquiry.invoices)) {
      inquiry.invoices = R.map((i) => upgradeInvoiceTax(i), inquiry.invoices || [])
    }

    const parsedSource = parseInquirySource(inquiry.inquirySource)

    inquiry.sourceType = parsedSource.type
    inquiry.sourceView = parsedSource.view

    if (inquiry.lastMessageAt) {
      inquiry.lastMessageAgo = timeAgo(inquiry.lastMessageAt)
    }

    inquiry.checkInStatus = getCheckInStatus(inquiry)

    inquiry.inquirySource = (inquiry.inquirySource + '').toLowerCase()

    inquiry.guestCount = (inquiry.numAdults || 0) + (inquiry.numChild || 0)

    inquiry.guestNameView = lodash.trim(R.pathOr('', ['name'], inquiry.guestDetails))

    inquiry.touchView = epochToView(inquiry.touch)
    inquiry.receivedOnView = epochToView(inquiry.receivedOn)
    inquiry.arriveView = epochToViewUTC(inquiry.guestArrive)
    inquiry.departView = epochToViewUTC(inquiry.guestDepart)
    inquiry.bookedView = epochToView(inquiry.booked)
    inquiry.createdView = epochToView(inquiry.created)
    inquiry.channelBookingIdView = inquiry.id !== inquiry.inquiryId ? inquiry.inquiryId : ''
    inquiry.paymentStatusView = getBookingPaymentStatus(inquiry)
    inquiry.nightsView = getNightsBetweenArrivalDepart(inquiry.guestArrive, inquiry.guestDepart)
    inquiry.guestsView = `${R.defaultTo(0, inquiry.numAdults) + R.defaultTo(0, inquiry.numChild)}`

    if (R.isEmpty(inquiry.notes) && inquiry.inquiryId !== inquiry.id) {
      const note = {} as NoteResponse
      note.key = uuid()
      note.note = `The ${inquiry.inquirySource} booking id is ${inquiry.inquiryId}`
      note.timestamp = inquiry.processedDate
      note.user = inquiry.inquirySource
      inquiry.notes = [note]
    }

    inquiry.automation = inquiry.automation || {}

    return inquiry
  }
}

export function getBookingPaymentStatus(inquiry: Inquiry) {
  return inquiry?.paid ? InquiryPaymentStatus.Paid : inquiry?.payment_status || InquiryPaymentStatus.Unpaid
}

// TV4-5863: only booked reservations that have reached their check-in date can be marked as in-house
export function isInhouseEligible(inquiry: Inquiry) {
  if (!inquiry?.booked || !!inquiry?.canceled) return false
  const checkIn = isSomething(inquiry.guestArrive) ? inquiry.guestArrive : inquiry.checkIn
  return isSomething(checkIn) && moment().unix() >= checkIn
}

export function getBookingGuestsCount(inquiry: Inquiry) {
  return (inquiry?.numAdults || 0) + (inquiry?.numChild || 0) || 1
}

export function isBookingFromExpediaAPI(inquiry: Inquiry) {
  if (!inquiry) return false
  return isImportedBooking(inquiry) && /expedia/gi.test(inquiry.inquirySource)
}

export function isImportedBooking(inquiry: Inquiry) {
  return inquiry.id !== inquiry.inquiryId
}

// TV4-5827: the backend double-booking guard flags an inquiry when a channel reservation is imported
// onto dates already taken on the same rental (availability sync to the channel likely failed). It sets
// attributes.double_booking (detection timestamp) and attributes.double_booking_source (e.g. 'booking.com').
export function isDoubleBooking(inquiry: Inquiry): boolean {
  return !!inquiry?.attributes?.double_booking
}

// TV4-6322: the inquiry mirrors a Booking.com Request to Book request.
export function isBdcRtbRequest(inquiry: Inquiry): boolean {
  return !!inquiry?.attributes?.bdc_rtb?.request_id
}

// TV4-6322: the host can still approve or decline the request.
export function isPendingBdcRtbRequest(inquiry: Inquiry): boolean {
  return isBdcRtbRequest(inquiry) && inquiry.attributes.bdc_rtb.request_status === 'CREATED'
}

export function isCardPaymentFailed(inquiry: Inquiry): boolean {
  return inquiry?.card_paid_status?.status === 'payment.card.rejected'
}

const doubleBookingSourceLabels: { [key: string]: string } = {
  'booking.com': 'Booking.com',
}

export function getDoubleBookingSourceView(inquiry: Inquiry): string {
  const source: string = inquiry?.attributes?.double_booking_source || ''
  if (!source) return ''
  return doubleBookingSourceLabels[source.toLowerCase()] || source.charAt(0).toUpperCase() + source.slice(1)
}

export function isBookingFromAirbnb(inquiry: Inquiry) {
  if (!inquiry) return false
  return isImportedBooking(inquiry) && /airbnb/gi.test(inquiry.inquirySource)
}

export function isBookingFromVrbo(inquiry: Inquiry) {
  if (!inquiry) return false
  return isImportedBooking(inquiry) && /vrbo/gi.test(inquiry.inquirySource)
}

export function isReadOnlyBooking(inquiry: Inquiry) {
  return inquiry?.inquirySource === 'marriott'
}

export function isInquiryFromAirbnb(inquiry: Inquiry) {
  if (!inquiry) return false
  return (
    inquiry.id === inquiry.inquiryId &&
    /airbnb/gi.test(inquiry.inquirySource) &&
    !isUUIDLose(inquiry.convoId) &&
    lodash.isNumber(inquiry.convoId)
  )
}

export interface LoadInquiriesFilter {
  rentals?: string[]
  source?: string
  status?: string
  period?: {
    from: number
    to: number
  }
  name?: string
  skip?: number
  limit?: number
}

export enum InquiryStatusEnum {
  Booked = 'booked',
  Canceled = 'canceled',
  Inquiry = 'inquiry',
}

export type InquiryStatus = 'preapproved' | 'inquiry' | 'denied' | 'deny' | 'accept' | 'pending' | 'cancelled_by_guest'

export function getCheckInStatus(inquiry: Inquiry) {
  const isCheckedIn = R.path(['attributes', 'is_checked_in'], inquiry) || inquiry?.inhouse > 0
  const isCheckedOut = R.path(['attributes', 'is_checked_out'], inquiry)

  if (isCheckedOut) {
    return CheckInStatus.CheckedOut
  }
  if (isCheckedIn) {
    return CheckInStatus.CheckedIn
  }

  let checkIn, checkOut
  if (isSomething(inquiry.checkIn) && isSomething(inquiry.checkOut)) {
    checkIn = inquiry.checkIn
    checkOut = inquiry.checkOut
  } else if (isSomething(inquiry.guestArrive) && isSomething(inquiry.guestDepart)) {
    checkIn = inquiry.guestArrive
    checkOut = inquiry.guestDepart
  }

  const currentTime = moment().unix()

  if (!checkIn || !checkOut || currentTime < checkIn) {
    return CheckInStatus.Arriving
  } else if (currentTime >= checkOut) {
    return CheckInStatus.CheckedOut
  } else if (currentTime < checkOut && currentTime >= checkIn) {
    return CheckInStatus.CheckedIn
  }
}
